/**
 * Supported barcode and QR code formats.
 */
export enum BarcodeFormat {
    /** Aztec 2D barcode format. */
    AZTEC = 0,
    /** CODABAR 1D format. */
    CODABAR = 1,
    /** Code 39 1D format. */
    CODE_39 = 2,
    /** Code 93 1D format. */
    CODE_93 = 3,
    /** Code 128 1D format. */
    CODE_128 = 4,
    /** Data Matrix 2D barcode format. */
    DATA_MATRIX = 5,
    /** EAN-8 1D format. */
    EAN_8 = 6,
    /** EAN-13 1D format. */
    EAN_13 = 7,
    /** ITF (Interleaved Two of Five) 1D format. */
    ITF = 8,
    /** MaxiCode 2D barcode format. */
    MAXICODE = 9,
    /** PDF417 format. */
    PDF_417 = 10,
    /** QR Code 2D barcode format. */
    QR_CODE = 11,
    /** RSS 14 */
    RSS_14 = 12,
    /** RSS EXPANDED */
    RSS_EXPANDED = 13,
    /** UPC-A 1D format. */
    UPC_A = 14,
    /** UPC-E 1D format. */
    UPC_E = 15,
    /** UPC/EAN extension format. Not a stand-alone format. */
    UPC_EAN_EXTENSION = 16,
    /** Try all supported formats. */
    TRY_ALL = 100
}

/**
 * Independent types for the CodeScanner API.
 * These types are decoupled from the underlying library (e.g., zxing).
 */

export interface ScannerResult {
    /**
     * Get the text content of the scanned code.
     */
    getText(): string;

    /**
     * Get the format of the scanned code (e.g., QR_CODE, EAN_13).
     */
    getFormat(): string;
}

export type ScanCallback = (result: ScannerResult) => void;

/**
 * Interface for the CodeScanner class.
 */
export interface ICodeScanner {
    /**
     * Start scanning from a DOM node.
     * @param domNode The DOM node where the scanner will be rendered.
     * @param deviceInfo The MediaDeviceInfo of the camera to use.
     * @param callback Function called when a result is found.
     * @param format Optional BarcodeFormat to search for. Defaults to BarcodeFormat.TRY_ALL.
     */
    startScanning(domNode: HTMLElement, deviceInfo: MediaDeviceInfo, callback: ScanCallback, format?: BarcodeFormat): Promise<void>;

    /**
     * Stop the scanner and release resources.
     */
    stopScanning(): void;

    /**
     * List available video input devices.
     */
    listVideoDevices(): Promise<MediaDeviceInfo[]>;
}

/**
 * Interface representing the global window structure with CodeScanner.
 */
export interface GlobalCodeScanner extends Window {
    CodeScanner: ICodeScanner;
    CodeScanners: {
        Get: () => ICodeScanner;
        listVideoDevices: () => Promise<MediaDeviceInfo[]>;
    };
}
